import React from "react";

import { SearchParameters } from "../../interfaces/SearchParameters.interface";
import BruksklasseSelect from "../Selection/BruksklasseSelect";
import AreaSelectArbeidMedVeglister from "./AreaSelect";

import { hasValidAreaInput } from "../../utils/validateAreaInput";

import * as R from "remeda";
import { removeSpace } from "../../utils/removeSpace";

import SubmitButton from "../Selection/SubmitButton";
import { AppState, UIModel, UIAction } from "../../store/ui";
import { ArbeidModel, ArbeidAction } from "../../store/arbeid";

import { SharedDatePicker } from "@nvdb-rapport/shared/src/components/DatePicker";
import SharedVegfilter from "@nvdb-rapport/shared/src/components/Vegfilter";

interface ArbeidMedVeglisterFormProps {
  arbeidState: ArbeidModel;
  arbeidDispatch: React.Dispatch<ArbeidAction>;
  uiState: UIModel;
  uiDispatch: React.Dispatch<UIAction>;
  onSearchClick: (params: SearchParameters) => void;
}

function ArbeidMedVeglisterForm({
  arbeidState,
  arbeidDispatch,
  uiState,
  uiDispatch,
  onSearchClick,
}: ArbeidMedVeglisterFormProps) {
  const filterState = arbeidState;
  const currentDate = arbeidState.date;
  const language = uiState.language;

  const onFormSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (hasValidInput && filterState.selectedAreaType !== undefined) {
      const params: SearchParameters = {
        areaType: filterState.selectedAreaType,
        bruksklasse: Number(filterState.selectedBruksklasse.value?.id),
        fylkesNummer: R.map(filterState.selectedFylker.value, f => f.nummer),
        kommuneNummer: R.map(filterState.selectedKommuner.value, f => f.nummer),
        date: filterState.date.unsafeCoerce(),
        vegsystemReferanse: removeSpace(filterState.vegsystemInput),
        spraak: language,
        publisering: false,
        V4: filterState.selectedReadVersion?.value ?? true,
      };
      uiDispatch({ type: "UpdateAppState", payload: AppState.DATA_PENDING });
      onSearchClick(params);
    } else {
      console.log("Ikke gyldig skjemadata");
      arbeidDispatch({
        type: "SetAreaTypeSelectError",
        payload: !hasValidAreaInput({
          fylker: R.map(filterState.selectedFylker.value, f => f.nummer),
          kommuner: R.map(filterState.selectedKommuner.value, k => k.nummer),
          selectedAreaType: filterState.selectedAreaType,
        }),
      });
    }
  };

  const hasValidInput = (): boolean => {
    // Valid if Bruksklasse and area is given, and Date and VegsystemRef have valid input

    return (
      filterState.selectedBruksklasse !== undefined &&
      filterState.date.isRight() &&
      !filterState.errorVegsystemRef
    );
  };

  const isValidated = R.allPass(filterState, [
    s => s.selectedBruksklasse.value !== undefined,
    s => s.date.isRight(),
    s => !s.errorVegsystemRef,
    s =>
      s.selectedAreaType === "fylker"
        ? s.selectedFylker.value.length > 0
        : s.selectedKommuner.value.length > 0,
  ]);

  return (
    <form method="post" onSubmit={onFormSubmit}>
      <BruksklasseSelect
        arbeidDispatch={arbeidDispatch}
        selectedBruksklasse={arbeidState.selectedBruksklasse}
        uiState={uiState}
        arbeidState={arbeidState}
        setSelectedBruksklasse={bruksklasse => {
          arbeidDispatch({
            type: "SelectBruksklasse",
            payload: bruksklasse,
          });
        }}
      />
      <AreaSelectArbeidMedVeglister
        arbeidDispatch={arbeidDispatch}
        uiState={uiState}
        arbeidState={arbeidState}
      />

      <SharedVegfilter
        vegsystem={arbeidState.vegsystem}
        vegsystemInput={arbeidState.vegsystemInput}
        updateVegsystem={value =>
          arbeidDispatch({ type: "UpdateVegsystem", payload: value })
        }
        variant="filled"
      />
      <SharedDatePicker
        selectDate={date =>
          arbeidDispatch({ type: "SelectDate", payload: date })
        }
        currentDate={currentDate}
        language={language}
      />
      {uiState.appState !== AppState.DATA_PENDING && (
        <SubmitButton disabled={!isValidated} />
      )}
    </form>
  );
}

export default ArbeidMedVeglisterForm;
